import { types, flow, getSnapshot, getRoot } from 'mobx-state-tree'
import { reaction } from 'mobx'
import BraintreeThreeDSecureStore from "./BraintreeThreeDSecureStore"
import * as PaymentService from '~/services/payments'

import theme from '~/style/theme'
import { REQUEST_STATUSES, PAYMENT_PROVIDERS } from '~/utilities/constants'

// The backend gates the Braintree client-token endpoint on phone verification
// and rejects with a phone-related message. Match it robustly (case-insensitive,
// both "verify" and "phone" present) instead of one exact string, so the
// phone-verify modal opens even if the backend wording/punctuation drifts.
const isPhoneVerificationError = err => {
  const message = String(err || '')
  return /verify/i.test(message) && /phone/i.test(message)
}


export const DEFAULT_HOSTED_FIELD_OPTIONS = {
  styles: {
    input: {
      'font-family': theme.fonts.avenirNextDemi,
      'font-size': '16px',
      color: theme.colors.text.input
    },
    'input.invalid': {
      color: theme.colors.error
    },
    '::-webkit-input-placeholder': {
      color: theme.colors.text.inputPlaceholder
    },
    ':-moz-placeholder': {
      color: theme.colors.text.inputPlaceholder
    },
    '::-moz-placeholder': {
      color: theme.colors.text.inputPlaceholder
    },
    ':-ms-input-placeholder': {
      color: theme.colors.text.inputPlaceholder
    }
  },
  fields: {
    number: {
      selector: '#card-number',
      rejectUnsupportedCards: true,
      placeholder: '•••• •••• •••• ••••'
    },
    cvv: {
      selector: '#cvv',
      placeholder: '•••'
    },
    expirationDate: {
      selector: '#expiration-date',
      placeholder: 'MM/YY'
    },
    postalCode: {
      selector: '#postal-code',
      placeholder: '11367'
    }
  }
}

const BRAINTREE_FORM_KEY_MAP = {
  postalCode: 'Postal code',
  cvv: 'CCV/CVV',
  expirationDate: 'Expiration date',
  number: 'Card number'
}

const BraintreeStore = types
  .model('BraintreeStore', {
    isReady: types.optional(types.boolean, false),
    isLoadingForm: types.optional(types.boolean, false),
    isValid: types.optional(types.boolean, false),
    clientOptions: types.frozen({}),
    nonce: types.maybeNull(types.string),
    clientToken: types.maybeNull(types.string),
    clientTokenStatus: types.optional(types.string, REQUEST_STATUSES.init),
    // hosted fields errors
    numberMessage: types.maybeNull(types.string),
    expirationDateMessage: types.maybeNull(types.string),
    cvvMessage: types.maybeNull(types.string),
    postalCodeMessage: types.maybeNull(types.string),
    threeDSecure: types.optional(BraintreeThreeDSecureStore, {})
  })
  .views(self => ({
    get isLoggedIn() {
      const rootStore = getRoot(self)
      return rootStore.auth.isLoggedIn
    }
  }))
  .actions(self => ({
    afterCreate() {
      if (!process.browser) return
      // If the Braintree token fetch was rejected because the user's phone
      // wasn't verified (clientTokenStatus === error), re-fetch the token and
      // rebuild the client once they verify — so the drop-in mounts without a
      // page refresh. No-op otherwise (reaction only fires on a change to
      // phoneNumberVerified, and is gated on the error status).
      reaction(
        () => {
          const user = getRoot(self).auth.user
          return Boolean(user && user.phoneNumberVerified)
        },
        phoneNumberVerified => {
          if (
            phoneNumberVerified &&
            self.clientTokenStatus === REQUEST_STATUSES.error
          ) {
            self.reinitialize()
          }
        }
      )
    },
  }))
  // volatile are things that are not serialized or tracked by MST. The things I store here are object pointers to created payment instances. These pointers can not be serialized, so they are referenced here.
  // eslint-disable-next-line
  .volatile(self => ({
    client: null,
    hostedFields: null,
    hostedFieldsOptions: {
      ...DEFAULT_HOSTED_FIELD_OPTIONS
    }
  }))
  .actions(self => ({
    setIsReady(value) {
      self.isReady = !!value
    },
    setIsLoadingForm(value) {
      self.isLoadingForm = !!value
    },
    setIsValid(value) {
      self.isValid = !!value
    },
    initialize: flow(function* initGen() {
      self.setIsReady(false)
      yield self.fetchToken()
      yield self.buildClient()
      // yield self.buildHostedFields() // Disabled in favor of BT drop-in form
      self.setIsReady(true)
    }),
    // Retry token fetch + client build after a recoverable failure (e.g. the
    // user has now verified their phone). Mirrors initialize but swallows the
    // buildClient throw so the afterCreate reaction can fire-and-forget it.
    reinitialize: flow(function* reinitGen() {
      try {
        yield self.initialize()
      } catch (err) {
        if (process.env.WINIT_ENV !== 'test') {
          console.error('braintree reinitialize failed', err)
        }
      }
    }),
    setNonce(nonce) {
      self.nonce = nonce
    },
    setClientToken(clientToken) {
      self.clientToken = clientToken
    },
    setClient(clientResponse) {
      self.client = clientResponse
    },
    setClientOptions(options) {
      self.clientOptions = options
    },
    setHostedFieldsOptions(options) {
      self.hostedFieldsOptions = options
    },
    buildClient: flow(function* buildCliGen() {
      const { clientOptions } = getSnapshot(self)
      const options = {
        ...clientOptions,
        authorization: self.clientToken
      }
      const baseClient = yield import('braintree-web/client')
      const clientResponse = yield baseClient.create(options)
      self.setClient(clientResponse)
    }),
    setHostedFields(hostedFieldsResponse) {
      self.hostedFields = hostedFieldsResponse
    },
    buildHostedFields: flow(function* buildHostFieGen() {
      const options = { ...self.hostedFieldsOptions }
      options.client = self.client
      const hostedFields = yield import('braintree-web/hosted-fields')
      const hostedFieldsResponse = yield hostedFields.create(options)
      self.setHostedFields(hostedFieldsResponse)
      self.hostedFields.on('validityChange', self.validityCheckEventHandler)
      self.hostedFields.on('blur', self.validityCheckEventHandler)
    }),
    fetchToken: flow(function* fetchTokenGen() {
      if (
        !self.clientToken &&
        self.clientTokenStatus !== REQUEST_STATUSES.loading
      ) {
        self.setClientTokenStatus(REQUEST_STATUSES.loading)
        try {
          let clientToken
          if (self.isLoggedIn) {
            const {auth} = getRoot(self)
            clientToken = yield PaymentService.getClientToken(
              auth.user.sessionToken,
              PAYMENT_PROVIDERS.braintree
            )
          } else {
            clientToken = yield PaymentService.getBasicBraintreeClientToken()
          }

          self.setClientToken(clientToken)
          self.setClientTokenStatus(REQUEST_STATUSES.complete)
          return true
        } catch (err) {
          if (process.env.WINIT_ENV !== 'test') {
            console.error('error fetching clientToken', err)
          }
          const all = getRoot(self)
          all.ui.toasts.addToast({
            message: `Issue fetching payment token: ${err}`,
            type: 'ERROR'
          })
          self.setClientTokenStatus(REQUEST_STATUSES.error)
          if (isPhoneVerificationError(err))
            all.ui.setPhoneVerifyModalShowing(true)
          return false
        }
      }
      return false
    }),
    setClientTokenStatus(status) {
      self.clientTokenStatus = status
    },
    tokenize() {
      // Disabled in favor of BT drop-in form:
      // return self.hostedFields.tokenize().then(data => {
      //   return data.nonce
      // })
      return Promise.resolve(self.nonce) // MST crashes if we don't return a promise here
    },
    validityCheckEventHandler(event) {
      let allValid = true
      const currentKey = event.emittedBy
      const currentKeyMesssageName = `${currentKey}Message`
      Object.keys(event.fields).forEach(key => {
        const field = event.fields[key]
        // checking if we should update allValid
        if (!field.isValid && allValid) {
          allValid = false
        }

        // check if we should set a message for the user
        if (currentKey === key) {
          if (field.isEmpty) {
            self[
              currentKeyMesssageName
            ] = `${BRAINTREE_FORM_KEY_MAP[currentKey]} is required.`
          } else if (!field.isValid) {
            self[
              currentKeyMesssageName
            ] = `${BRAINTREE_FORM_KEY_MAP[currentKey]} is invalid.`
          } else if (field.isValid) {
            self[currentKeyMesssageName] = null
          }
        }
      })
      self.setIsValid(allValid)
    },
    teardown() {
      self.setIsReady(false)
      self.setIsValid(false)
      if (self.hostedFields) {
        self.hostedFields.teardown()
        self.hostedFields = null
      }
    },
    validateForm() {
      if (!self.hostedFields) return false
      const state = self.hostedFields.getState()
      let allValid = true
      Object.keys(state.fields).forEach(key => {
        const currentKey = key
        const currentKeyMesssageName = `${key}Message`
        const field = state.fields[key]
        // checking if we should update allValid
        if (!field.isValid && allValid) {
          allValid = false
        }

        // check if we should set a message for the user
        if (currentKey === key) {
          if (field.isEmpty) {
            self[
              currentKeyMesssageName
            ] = `${BRAINTREE_FORM_KEY_MAP[currentKey]} is required.`
          } else if (!field.isValid) {
            self[
              currentKeyMesssageName
            ] = `${BRAINTREE_FORM_KEY_MAP[currentKey]} is invalid.`
          } else if (field.isValid) {
            self[currentKeyMesssageName] = null
          }
        }
      })
      self.setIsValid(allValid)
      return true
    }
  }))

export default BraintreeStore
